import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Category {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  parentId: string | null;
  parentName: string | null;
  parentSlug: string | null;
  articleCount: number;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

export function AdminKbCategoriesPage(): React.ReactElement {
  useDocumentTitle('KB categories');
  const { agent } = useAuth();

  const [categories, setCategories] = useState<Category[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [parentId, setParentId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<{ categories: Category[] }>('/api/kb/categories');
      setCategories(r.categories);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load categories.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setBusy(true);
    try {
      await apiFetch('/api/kb/categories', {
        method: 'POST',
        json: {
          name: name.trim(),
          slug: (slug || slugify(name)).trim(),
          parentId: parentId || null,
        },
      });
      setName('');
      setSlug('');
      setSlugTouched(false);
      setParentId('');
      setAdding(false);
      await load();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create category.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <p className="text-sm text-[var(--color-danger)]">{loadError}</p>;
  if (!categories) return <p className="text-[var(--color-muted)]">Loading…</p>;

  const topLevel = categories.filter((c) => c.parentId === null);
  const subsByParent = new Map<string, Category[]>();
  for (const c of categories) {
    if (c.parentId) {
      const arr = subsByParent.get(c.parentId) ?? [];
      arr.push(c);
      subsByParent.set(c.parentId, arr);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">KB categories</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Two levels deep — top-level categories show as tiles on the portal landing, and their
          subcategories show as tiles on the parent's page. Articles can sit in either level.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'New category'}</Button>
      </div>

      {adding && (
        <Card>
          <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Name"
              name="name"
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(slugify(e.target.value));
              }}
            />
            <FormField
              label="Slug"
              name="slug"
              required
              value={slug}
              hint="URL fragment, lowercase + dashes."
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugTouched(true);
              }}
            />
            <Select
              label="Parent (optional)"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">— top-level —</option>
              {topLevel.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            <div className="sm:col-span-2">
              {createError && (
                <p className="mb-2 text-sm text-[var(--color-danger)]">{createError}</p>
              )}
              <Button type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {topLevel.length === 0 ? (
        <Card>
          <p className="text-center text-[var(--color-muted)]">
            No categories yet. Add one to start grouping KB articles.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {topLevel.map((parent) => (
            <CategoryRow
              key={parent.id}
              category={parent}
              subcategories={subsByParent.get(parent.id) ?? []}
              allTopLevel={topLevel}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryRow({
  category,
  subcategories,
  allTopLevel,
  onChanged,
}: {
  category: Category;
  subcategories: Category[];
  allTopLevel: Category[];
  onChanged: () => Promise<void>;
}): React.ReactElement {
  return (
    <Card>
      <CategoryEditableLine category={category} allTopLevel={allTopLevel} onChanged={onChanged} />
      {subcategories.length > 0 && (
        <ul className="mt-3 space-y-2 border-l-2 border-[var(--color-border)] pl-4">
          {subcategories.map((sub) => (
            <li key={sub.id}>
              <CategoryEditableLine
                category={sub}
                allTopLevel={allTopLevel}
                onChanged={onChanged}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CategoryEditableLine({
  category,
  allTopLevel,
  onChanged,
}: {
  category: Category;
  allTopLevel: Category[];
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [slug, setSlug] = useState(category.slug);
  const [parentId, setParentId] = useState<string>(category.parentId ?? '');
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/kb/categories/${category.id}`, {
        method: 'PATCH',
        json: {
          name: name.trim(),
          slug: slug.trim(),
          parentId: parentId || null,
          sortOrder: Number(sortOrder) || 0,
        },
      });
      setEditing(false);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !window.confirm(
        `Delete "${category.name}"? Any subcategories will be deleted too. Articles will become uncategorized.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/kb/categories/${category.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete.');
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-semibold text-[var(--color-header)]">{category.name}</div>
          <div className="text-xs text-[var(--color-muted)]">
            slug: {category.slug} · sort: {category.sortOrder} · {category.articleCount}{' '}
            {category.articleCount === 1 ? 'article' : 'articles'}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          <Button variant="outline" onClick={() => void remove()} disabled={busy}>
            {busy ? 'Working…' : 'Delete'}
          </Button>
        </div>
        {error && (
          <p className="ml-2 text-sm text-[var(--color-danger)]">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-12">
      <div className="sm:col-span-4">
        <FormField label="Name" name="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="sm:col-span-3">
        <FormField
          label="Slug"
          name="slug"
          value={slug}
          onChange={(e) => setSlug(slugify(e.target.value))}
        />
      </div>
      <div className="sm:col-span-3">
        <Select
          label="Parent"
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">— top-level —</option>
          {allTopLevel
            .filter((p) => p.id !== category.id)
            .map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
        </Select>
      </div>
      <div className="sm:col-span-2">
        <FormField
          label="Sort"
          name="sort"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </div>
      <div className="flex items-end gap-2 sm:col-span-12">
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>
          Cancel
        </Button>
        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
      </div>
    </div>
  );
}
